/**
 * Utility functions for Customer Performance module
 */

import { CustomerProfile, CustomerRatingThreshold } from '../../lib/api';
import { GradeInfo } from './types';

/**
 * Get grade info based on combined score and configured thresholds
 */
export function getGradeInfo(
  combinedScore: number | undefined | null,
  ratingThresholds: CustomerRatingThreshold[]
): GradeInfo {
  const score = combinedScore ?? 0;

  // Find matching threshold
  for (const threshold of ratingThresholds) {
    const minScore = parseFloat(threshold.min_score);
    const maxScore = parseFloat(threshold.max_score);
    if (score >= minScore && score < maxScore) {
      return {
        grade: threshold.grade,
        label: threshold.display_label || threshold.label || threshold.grade,
        color: threshold.color,
      };
    }
  }

  // Fallback: check if score equals max of any threshold (for 100%)
  for (const threshold of ratingThresholds) {
    const maxScore = parseFloat(threshold.max_score);
    if (score === maxScore) {
      return {
        grade: threshold.grade,
        label: threshold.display_label || threshold.label || threshold.grade,
        color: threshold.color,
      };
    }
  }

  // Default fallback if no thresholds configured
  if (ratingThresholds.length === 0) {
    if (score >= 90) return { grade: 'A', label: 'A', color: '#22C55E' };
    if (score >= 75) return { grade: 'B', label: 'B', color: '#84CC16' };
    if (score >= 60) return { grade: 'C', label: 'C', color: '#EAB308' };
    if (score >= 40) return { grade: 'D', label: 'D', color: '#F97316' };
    return { grade: 'F', label: 'F', color: '#EF4444' };
  }

  // Return lowest grade if nothing matches
  const lowest = ratingThresholds[ratingThresholds.length - 1];
  return {
    grade: lowest?.grade || 'F',
    label: lowest?.display_label || lowest?.label || lowest?.grade || 'F',
    color: lowest?.color || '#EF4444',
  };
}

/**
 * Get letter grade from OTIF score
 */
export function getLetterGrade(
  otifScore: number | undefined,
  ratingThresholds: CustomerRatingThreshold[]
): string {
  return getGradeInfo(otifScore, ratingThresholds).label;
}

/**
 * Get rating badge style
 */
export function getRatingStyle(
  otifScore: number | undefined,
  ratingThresholds: CustomerRatingThreshold[]
): { backgroundColor: string; color: string } {
  const info = getGradeInfo(otifScore, ratingThresholds);
  return {
    backgroundColor: `${info.color}20`,
    color: info.color,
  };
}

/**
 * Get OTD color class based on rate
 */
export function getOTDColor(rate: number): string {
  if (rate >= 90) return 'text-green-600 dark:text-green-400';
  if (rate >= 80) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

/**
 * Format currency value from backend.
 * Backend returns values already converted to user's preferred currency.
 */
export function formatCurrency(value: string | null, currency: string): string {
  if (!value) return '0';

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '0';

  const currencySymbol =
    currency === 'GBP' ? '£'
      : currency === 'EUR' ? '€'
        : currency === 'USD' ? '$'
          : currency === 'PLN' ? 'PLN '
            : currency + ' ';

  if (numValue >= 1000000) {
    return `${currencySymbol}${(numValue / 1000000).toFixed(2)}M`;
  } else if (numValue >= 1000) {
    return `${currencySymbol}${(numValue / 1000).toFixed(2)}K`;
  }

  return `${currencySymbol}${numValue.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
}

/**
 * Check if customer has N/A rating (no combined score data)
 */
export function isNARating(customer: CustomerProfile): boolean {
  return customer.combined_score === null || customer.combined_score === undefined;
}
